import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, ExternalLink, Filter } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function authHeaders() {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
}

const MODEL_OPTIONS = [
  { id: 'all', label: 'All models' },
  { id: 'nano-banana-pro', label: 'Nano Banana Pro' },
  { id: 'gpt-image-2', label: 'GPT Image-2' },
  { id: 'seedance-2', label: 'Seedance 2.0' },
];

const STATUS_OPTIONS = [
  { id: 'all', label: 'All statuses' },
  { id: 'success', label: 'Success' },
  { id: 'fail', label: 'Failed' },
  { id: 'generating', label: 'Generating' },
  { id: 'queued', label: 'Queued' },
];

function statusColor(s) {
  if (s === 'success') return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
  if (s === 'fail') return 'bg-red-500/15 text-red-400 border-red-500/30';
  if (s === 'generating' || s === 'queuing' || s === 'waiting') return 'bg-blue-500/15 text-blue-400 border-blue-500/30';
  return 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30';
}

export default function HistoryTab() {
  const { user } = useAuth();
  const isOwner = user?.role === 'admin';

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [modelFilter, setModelFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vaEmailFilter, setVaEmailFilter] = useState('all');
  const [vaList, setVaList] = useState([]);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    try {
      setLoading(true);
      const params = { limit: 200 };
      if (modelFilter !== 'all') params.model_id = modelFilter;
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      if (isOwner && vaEmailFilter !== 'all') params.va_email = vaEmailFilter;
      const res = await axios.get(`${API_URL}/generation/history`, {
        headers: authHeaders(),
        params,
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);

      // Extract distinct VAs for the owner filter
      if (isOwner) {
        const seen = new Map();
        (res.data.items || []).forEach((it) => {
          if (it.user_email && !seen.has(it.user_email)) {
            seen.set(it.user_email, it.user_name || it.user_email);
          }
        });
        setVaList(Array.from(seen.entries()).map(([email, name]) => ({ email, name })));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [modelFilter, statusFilter, vaEmailFilter]);

  const refreshOne = async (generationId) => {
    try {
      const res = await axios.get(`${API_URL}/generation/task/${generationId}`, { headers: authHeaders() });
      setItems((prev) => prev.map((it) => (it.generation_id === generationId ? res.data : it)));
    } catch (e) {
      toast.error('Refresh failed');
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card className="bg-zinc-950 border-zinc-800">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-zinc-400">
            <Filter className="w-4 h-4" strokeWidth={1.5} />
            <span>Filters</span>
          </div>

          <Select value={modelFilter} onValueChange={setModelFilter}>
            <SelectTrigger className="w-[180px] bg-zinc-950 border-zinc-800" data-testid="history-model-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MODEL_OPTIONS.map((o) => (
                <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] bg-zinc-950 border-zinc-800" data-testid="history-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {isOwner && (
            <Select value={vaEmailFilter} onValueChange={setVaEmailFilter}>
              <SelectTrigger className="w-[220px] bg-zinc-950 border-zinc-800" data-testid="history-va-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All users</SelectItem>
                {vaList.map((v) => (
                  <SelectItem key={v.email} value={v.email}>
                    {v.name} <span className="text-zinc-500">({v.email})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-zinc-500">{items.length} of {total}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              className="border-zinc-700 text-zinc-200 hover:bg-zinc-900"
              data-testid="history-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      {items.length === 0 ? (
        <div className="text-sm text-zinc-500 py-12 text-center border border-dashed border-zinc-800 rounded-md">
          No generations yet.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const isOpen = expanded[it.generation_id];
            return (
              <Card key={it.generation_id} className="bg-zinc-950 border-zinc-800">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-semibold text-sm">{it.model_label}</span>
                        <Badge variant="outline" className={`text-xs ${statusColor(it.status)}`}>
                          {it.status}
                        </Badge>
                        <span className="text-xs text-zinc-500">
                          {new Date(it.created_at).toLocaleString()}
                        </span>
                        {isOwner && (
                          <span className="text-xs text-zinc-500">
                            · {it.user_name || it.user_email} {it.va_role ? `(${it.va_role})` : ''}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-zinc-300 mt-1 line-clamp-2">
                        {it.prompt}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-white">
                        ${(it.cost_usd || 0).toFixed(3)}
                      </span>
                      {(it.status === 'queued' || it.status === 'generating' || it.status === 'queuing' || it.status === 'waiting') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => refreshOne(it.generation_id)}
                          className="border-zinc-700 text-zinc-200 hover:bg-zinc-900"
                        >
                          <RefreshCw className="w-3 h-3" />
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setExpanded((p) => ({ ...p, [it.generation_id]: !p[it.generation_id] }))}
                        className="border-zinc-700 text-zinc-200 hover:bg-zinc-900"
                        data-testid={`history-toggle-${it.generation_id}`}
                      >
                        {isOpen ? 'Hide' : 'View'}
                      </Button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="mt-4 pt-4 border-t border-zinc-800 space-y-3">
                      {it.status === 'success' && (it.result_urls || []).length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {it.result_urls.map((u, i) => (
                            <div key={u + i} className="border border-zinc-800 rounded-md overflow-hidden bg-zinc-950">
                              {it.kind === 'video' ? (
                                <video src={u} controls className="w-full h-auto" />
                              ) : (
                                <img src={u} alt="" className="w-full h-auto" />
                              )}
                              <a
                                href={u}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center justify-between px-2 py-1 text-xs text-zinc-300 hover:bg-zinc-900"
                              >
                                Open
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          ))}
                        </div>
                      )}

                      {it.status === 'fail' && (
                        <div className="text-sm text-red-400">Error: {it.error}</div>
                      )}

                      <details className="text-xs text-zinc-400">
                        <summary className="cursor-pointer hover:text-white">Params</summary>
                        <pre className="mt-2 p-2 bg-zinc-950 border border-zinc-800 rounded overflow-auto">
{JSON.stringify(it.params, null, 2)}
                        </pre>
                      </details>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
